import { api } from '@/lib/api/client'
import type { AffiliateAllowedCampaign } from '@/types/affiliates'
import type {
  FetchMyAffiliateBalanceTransactionsResponse,
  FetchMyAffiliatePaymentsResponse,
  FetchMyAffiliateProgramsResponse,
  MyAffiliateLoginContext,
  MyAffiliateProfile,
  MyAffiliateProgram,
} from '@/types/my-affiliate'

export interface MyAffiliateEmptyStateMessage {
  lines: string[]
  authHint?: string
}

export function describeMyAffiliateEmptyState(
  loginContext: MyAffiliateLoginContext | undefined,
  profile: MyAffiliateProfile | null,
  programs: MyAffiliateProgram[]
): MyAffiliateEmptyStateMessage {
  const authEmail = loginContext?.auth_email?.trim() || null
  const authHint = authEmail
    ? `Conta logada: ${authEmail}`
    : loginContext?.auth_phone
      ? `Conta logada (telefone): ${loginContext.auth_phone}`
      : undefined

  const onlyInactive =
    profile !== null && programs.length > 0 && programs.every((p) => !p.is_active)

  if (onlyInactive) {
    return {
      lines: [
        'Você possui credenciamento de afiliado, mas está inativo no momento.',
        'Peça à instituição para reativar seu perfil de afiliado.',
      ],
      authHint,
    }
  }

  if (loginContext && !loginContext.platform_user_resolved) {
    return {
      lines: [
        'Não encontramos um usuário da plataforma vinculado ao e-mail ou telefone desta conta.',
        'Para ver Sou Afiliado, entre com o mesmo e-mail (ou telefone) que a instituição usou ao te credenciar.',
        'Se você entrou com o e-mail da empresa (instituição), isso é diferente do e-mail pessoal credenciado como afiliado.',
      ],
      authHint,
    }
  }

  if (profile !== null && programs.length === 0) {
    const linked = profile.email?.trim() || profile.phone || 'sua conta'
    return {
      lines: [
        `Sua identidade de afiliado (${linked}) está cadastrada, mas nenhuma instituição aparece credenciada.`,
        'Confirme com a instituição se o credenciamento foi concluído e está ativo.',
      ],
      authHint,
    }
  }

  return {
    lines: [
      'Você ainda não está credenciado como afiliado em nenhuma instituição.',
      'O credenciamento é feito pela instituição em Meus Afiliados.',
      'Depois, acesse Sou Afiliado com o mesmo e-mail ou telefone usado na busca do credenciamento.',
    ],
    authHint,
  }
}

/** Credenciado como afiliado em ao menos uma instituição (programa Sou Afiliado). */
export function userHasAffiliateParticipation(
  programs: MyAffiliateProgram[] | undefined
): boolean {
  return Array.isArray(programs) && programs.length > 0
}

export async function fetchMyAffiliatePrograms(): Promise<FetchMyAffiliateProgramsResponse> {
  return api.get<FetchMyAffiliateProgramsResponse>('/api/my-affiliate/programs', {
    requireAuth: true,
  })
}

export async function fetchMyAffiliatePaymentsAll(
  params: {
    affiliate_id?: number
    campaign_id?: number
    page?: number
    per_page?: number
  } = {}
): Promise<FetchMyAffiliatePaymentsResponse> {
  const searchParams = new URLSearchParams()
  if (params.affiliate_id != null) {
    searchParams.set('affiliate_id', String(params.affiliate_id))
  }
  if (params.campaign_id != null) searchParams.set('campaign_id', String(params.campaign_id))
  if (params.page != null) searchParams.set('page', String(params.page))
  if (params.per_page != null) searchParams.set('per_page', String(params.per_page))
  const query = searchParams.toString()

  return api.get<FetchMyAffiliatePaymentsResponse>(
    `/api/my-affiliate/payments${query ? `?${query}` : ''}`,
    { requireAuth: true }
  )
}

export async function fetchMyAffiliatePayments(
  affiliateId: number,
  params: { campaign_id?: number; page?: number; per_page?: number } = {}
): Promise<FetchMyAffiliatePaymentsResponse> {
  const searchParams = new URLSearchParams()
  if (params.campaign_id != null) {
    searchParams.set('campaign_id', String(params.campaign_id))
  }
  if (params.page != null) searchParams.set('page', String(params.page))
  if (params.per_page != null) searchParams.set('per_page', String(params.per_page))
  const query = searchParams.toString()

  return api.get<FetchMyAffiliatePaymentsResponse>(
    `/api/my-affiliate/programs/${affiliateId}/payments${query ? `?${query}` : ''}`,
    { requireAuth: true }
  )
}

export async function fetchMyAffiliateBalanceTransactions(
  affiliateId: number,
  params: { page?: number; per_page?: number } = {}
): Promise<FetchMyAffiliateBalanceTransactionsResponse> {
  const searchParams = new URLSearchParams()
  if (params.page != null) searchParams.set('page', String(params.page))
  if (params.per_page != null) searchParams.set('per_page', String(params.per_page))
  const query = searchParams.toString()

  return api.get<FetchMyAffiliateBalanceTransactionsResponse>(
    `/api/my-affiliate/programs/${affiliateId}/balance-transactions${query ? `?${query}` : ''}`,
    { requireAuth: true }
  )
}

function resolveHttpsExternalPageUrl(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? ''
  if (trimmed === '') {
    return null
  }

  try {
    const url = new URL(trimmed)
    if (url.protocol !== 'https:') {
      return null
    }

    return url.toString()
  } catch {
    return null
  }
}

export function buildMyAffiliateLink(
  campaign: Pick<AffiliateAllowedCampaign, 'slug' | 'public_page_url'>,
  trackingCode: string,
  origin: string = typeof window !== 'undefined' ? window.location.origin : ''
): string {
  const externalBase = resolveHttpsExternalPageUrl(campaign.public_page_url)
  if (externalBase !== null) {
    const url = new URL(externalBase)
    url.searchParams.set('af', trackingCode)

    return url.toString()
  }

  return `${origin}/campanha/${campaign.slug}?af=${encodeURIComponent(trackingCode)}`
}

export function formatMyAffiliateCurrency(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

export function activePrograms(programs: MyAffiliateProgram[]): MyAffiliateProgram[] {
  return programs.filter((p) => p.is_active)
}

export function manualPrograms(programs: MyAffiliateProgram[]): MyAffiliateProgram[] {
  return programs.filter((p) => p.payout_method === 'manual')
}
